/**
 * KOL-82: decide which pending sender-key envelopes may be acknowledged.
 *
 * Acking deletes the envelope on the server. The old code acked whenever
 * decryptMessage() did not throw — but decrypting the envelope and actually
 * storing the sender key are two different things. When the second half
 * failed silently the key was gone from the server and absent on the client,
 * and the next message started the same loop again: measured 17.08. on
 * Richie — 10 redistribute calls, 5 fetches, 12 acks in half an hour, and
 * the browser still could not read a single bot message. Frau Knöbel hit the
 * same loop in the customer channel on 14.08.
 *
 * Rule: only ack what demonstrably landed in storage. Envelopes that
 * decrypted but produced no key stay on the server for the next attempt —
 * unless they keep failing, in which case they are genuinely dead (a
 * one-time pre-key consumed on first delivery can never decrypt again) and
 * would otherwise be refetched forever.
 */

export const DEAD_ENVELOPE_ATTEMPTS = 3;

export function decideAck(opts: {
  /** ids whose envelope decrypted AND whose key is now in storage */
  verified: string[];
  /** ids whose envelope decrypted but left no key behind */
  unverified: string[];
  /** ids whose envelope did not decrypt at all */
  failed: string[];
  /** how often this channel already ran a full-failure round */
  failureRound: number;
}): { ack: string[]; keepForRetry: string[] } {
  const { verified, unverified, failed, failureRound } = opts;
  if (verified.length > 0) {
    // Progress: ack what worked, leave the rest for the next round.
    return { ack: verified, keepForRetry: [...unverified, ...failed] };
  }
  if (failureRound + 1 >= DEAD_ENVELOPE_ATTEMPTS) {
    // Nothing worked repeatedly — purge so the client stops refetching.
    return { ack: [...unverified, ...failed], keepForRetry: [] };
  }
  return { ack: [], keepForRetry: [...unverified, ...failed] };
}
